import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  BOOKING_DOC_TYPE,
  BOOKING_STAGE,
  BOOKING_STATUS,
} from "constants/general";
import { ROUTES } from "constants/routes";
import { useState } from "react";
import Dropzone from "react-dropzone";
import { useNavigate } from "react-router-dom";
import imageService from "services/image.service";
import { updateBookingStatus, uploadBookingDocs } from "slices/bookingReducer";
import { handleSnackBar } from "slices/global";
import { updateSelectedBookingData } from "slices/rentalsReducer";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { getImageAsDataUrl } from "utils/image.util";
import { getCurrentTimestamp } from "utils/timestamp.util";

const BookingDocumentsUpload = ({
  uploadMode,
  setUploadMode,
}: {
  uploadMode: string;
  setUploadMode: any;
}) => {
  const navigate = useNavigate();
  const { rentalsReducer } = useAppSelector((state) => state);
  const { selectedBookingData }: any = rentalsReducer;
  const dispatch = useAppDispatch();
  const [docs, setDocs] = useState<Array<any>>([]);
  const [images, setImages] = useState<Array<any>>([]);
  const [uploadImages, setUploadImages] = useState(false);
  const bookingStage =
    uploadMode === "Departure" ? BOOKING_STAGE.DEPARTURE : BOOKING_STAGE.RETURN;
  const bookingId = selectedBookingData.id;

  const UploadButton = ({
    mimeType,
    allowMultiple = false,
    id,
    onchange,
  }: {
    mimeType: any;
    allowMultiple: boolean;
    id: string;
    onchange: any;
  }) => {
    const [dragActive, setDragActive] = useState(false);
    return (
      <Dropzone
        key={id}
        onDrop={(acceptedFiles) => {
          console.log(acceptedFiles);
          onchange(acceptedFiles);
        }}
        multiple={allowMultiple}
        // accept={{ "image/*": [], }}
        onDragEnter={() => setDragActive(true)}
        onDragLeave={() => setDragActive(false)}
      >
        {({ getRootProps, getInputProps }) => (
          <Box
            sx={{
              border: "1px dashed #307AFF",
              height: {
                md: "192px",
                lg: "192px",
                xl: "192px",
                sm: "125px",
                xs: "125px",
              },
              width: {
                md: "192px",
                lg: "192px",
                xl: "192px",
                sm: "125px",
                xs: "125px",
              },
              // padding: "10px",
              display: "flex",
              justifyContent: "center",
              background: "#F4F4F4",
              borderRadius: "10px",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              strokeWidth: "2px",
            }}
          >
            <div
              {...getRootProps()}
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <input {...getInputProps()} />
              <img
                src="/assets/img/icons/gallery_export.svg"
                alt=""
                width="32px"
                height="32px"
              />
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: {
                    md: "16px",
                    lg: "16px",
                    xl: "16px",
                    sm: "13px",
                    xs: "13px",
                  },
                  lineHeight: {
                    md: "24px",
                    lg: "24px",
                    xl: "24px",
                    sm: "20px",
                    xs: "20px",
                  },
                  color: "#A2A2A2",
                  textAlign: "center",
                }}
              >
                Drag and drop an file here or{" "}
                <Typography
                  component="span"
                  sx={{ color: "#307AFF", textDecoration: "underline" }}
                >
                  click
                </Typography>
              </Typography>
            </div>
          </Box>
        )}

        {/* <input
            type="file"
            name={id}
            id={id}
            accept={mimeType.join(",")}
            hidden
            onChange={onchange}
            multiple={allowMultiple}
          /> */}
      </Dropzone>
    );
  };

  const handleImageChange = (e: any) => {
    if (images.length === 8) {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Maximum 8 files allowed.",
          severity: "error",
        })
      );
      return;
    }

    const imagesToKeep = 8 - images.length;
    if (e.length > imagesToKeep) {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Maximum 8 files allowed.",
          severity: "error",
        })
      );
      return;
    }
    getImageAsDataUrl(
      e,
      (data: Array<any>) =>
        setImages((prev: Array<any>) => [
          ...data.map((d: any) => ({ ...d, type: BOOKING_DOC_TYPE.IMAGES })),
          ...prev,
        ]),
      8
    );
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_: any, i: number) => i !== index));
  };

  const handleDocChange = (e: any) => {
    if (e.length === 0) return;
    var ext = e[0].name.split(".").pop();
    var type = e[0].type;
    if (
      ext == "pdf" ||
      ext == "docx" ||
      ext == "doc" ||
      type.includes("image")
    ) {
      const input = e;
      setDocs([
        {
          name: input[0].name,
          file: input[0],
          type: BOOKING_DOC_TYPE.FORM,
        },
      ]);
    } else {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Only PDF, doc, image files are allowed.",
          severity: "error",
        })
      );
      return;
    }
  };

  const removeDocFile = (index: number) => {
    setDocs((prev) => prev.filter((_: any, i: number) => i !== index));
  };

  const proceedToSaveFiles = async () => {
    if (images.length < 5) {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Minimum 5 images is required.",
          severity: "error",
        })
      );
      return;
    }
    if (docs.length < 1) {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Form can not be empty.",
          severity: "error",
        })
      );
      return;
    }
    handleUploadImage();
    const storagePath = `bookings/${bookingId}`;
    const files = [...images, ...docs];
    const resp = [];

    for (const fi of files) {
      try {
        const uploadResp = await imageService.UploadImageToStorage(
          [fi.file],
          `${storagePath}/${getCurrentTimestamp()}_${fi.file.name.replaceAll(
            " ",
            "_"
          )}`
        );

        resp.push({ uploadResp, status: true, ...fi });
      } catch (err: any) {
        console.log("Uploading error");
      }
    }

    const formsData = resp
      .filter((r: any) => r.type === BOOKING_DOC_TYPE.FORM)
      .map((d: any) => ({
        bucket: d.uploadResp.bucket,
        full_path: d.uploadResp.full_path,
        name: d.uploadResp.name,
        size: d.uploadResp.size,
        content_type: d.uploadResp.content_type,
        preview_url: d.uploadResp.preview_url,
        document_type: BOOKING_DOC_TYPE.FORM,
      }));

    const imagesData = resp
      .filter((r: any) => r.type === BOOKING_DOC_TYPE.IMAGES)
      .map((d: any) => ({
        bucket: d.uploadResp.bucket,
        full_path: d.uploadResp.full_path,
        name: d.uploadResp.name,
        size: d.uploadResp.size,
        content_type: d.uploadResp.content_type,
        preview_url: d.uploadResp.preview_url,
        document_type: BOOKING_DOC_TYPE.IMAGES,
      }));

    const formPayload = {
      booking_stage: bookingStage,
      id: bookingId,
      documents: formsData,
    };

    const imagePayload = {
      booking_stage: bookingStage,
      id: bookingId,
      documents: imagesData,
    };

    dispatch(uploadBookingDocs({ data: formPayload, onSucess: () => {} }));
    dispatch(
      updateBookingStatus({
        data: {
          id: bookingId,
          status: 8, // Booking STARTED
        },
        onSucess: async () => {
          dispatch(
            uploadBookingDocs({
              data: imagePayload,
              onSucess: () => {
                if (bookingStage === BOOKING_STAGE.RETURN) {
                  dispatch(
                    updateBookingStatus({
                      data: {
                        id: bookingId,
                        status: BOOKING_STATUS.RETURNED,
                      },
                      onSucess: () => {
                        dispatch(
                          updateSelectedBookingData({
                            id: bookingId,
                            data: {
                              booking_status: BOOKING_STATUS.RETURNED,
                            },
                          })
                        );
                        navigate(
                          `${ROUTES.RENTALS}?type=return&booking=${selectedBookingData.id}`
                        );
                      },
                    })
                  );
                } else {
                  dispatch(
                    updateSelectedBookingData({
                      id: bookingId,
                      data: {
                        booking_status: BOOKING_STATUS.STARTED,
                      },
                    })
                  );
                }
                dispatch(
                  updateSelectedBookingData({
                    id: bookingId,
                    data: {
                      booking_documents: [
                        ...(selectedBookingData?.booking_documents ?? []),
                        ...imagesData.map((r: any) => ({
                          ...r,
                          booking_stage: bookingStage,
                        })),
                        ...formsData.map((r: any) => ({
                          ...r,
                          booking_stage: bookingStage,
                        })),
                      ],
                    },
                  })
                );
                setUploadMode("");
                handleUploadImage();
              },
            })
          );
        },
      })
    );
  };

  const handleUploadImage = () => setUploadImages(!uploadImages);
  return (
    <Box
      sx={{
        padding: {
          md: "20px 60px 40px 60px",
          lg: "20px 60px 40px 60px",
          xl: "20px 60px 40px 60px",
          sm: "15px 10px",
          xs: "15px 10px",
        },
      }}
    >
      <input
        autoFocus
        style={{
          opacity: 0,
          height: "1px",
          padding: "0px",
          margin: "0px",
        }}
      />
      <Box mb="30px">
        <Typography
          sx={{
            color: "#323232",
            fontSize: "24px",
            fontWeight: 600,
            lineHeight: "35px",
          }}
        >
          Upload {uploadMode} Images
        </Typography>
        <Typography
          sx={{
            color: "#A2A2A2",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "24px",
          }}
        >
          We require few images for your Equipment.( Add minimum 5 images)
        </Typography>
        <Box sx={{ mt: "20px" }}>
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "31px",
            }}
          >
            Images
          </Typography>

          <Box
            sx={{ mt: "15px", display: "flex", gap: "20px", flexWrap: "wrap" }}
          >
            <UploadButton
              mimeType={["image/*", "video/*"]}
              allowMultiple={true}
              id="images"
              onchange={handleImageChange}
            />
            {images.map((url, i) => (
              <Box
                sx={{
                  position: "relative",
                  height: {
                    md: "192px",
                    lg: "192px",
                    xl: "192px",
                    sm: "125px",
                    xs: "125px",
                  },
                  width: {
                    md: "192px",
                    lg: "192px",
                    xl: "192px",
                    sm: "125px",
                    xs: "125px",
                  },
                  borderRadius: "10px",
                }}
              >
                <Box
                  component="img"
                  src={url.dataUrl}
                  sx={{
                    height: {
                      md: "192px",
                      lg: "192px",
                      xl: "192px",
                      sm: "125px",
                      xs: "125px",
                    },
                    width: {
                      md: "192px",
                      lg: "192px",
                      xl: "192px",
                      sm: "125px",
                      xs: "125px",
                    },
                    borderRadius: "10px",
                  }}
                />
                <Box
                  sx={{ position: "absolute", top: "-10px", right: "-10px" }}
                  onClick={() => removeImage(i)}
                >
                  <img
                    src="/assets/img/icons/close_red.svg"
                    width="24"
                    height="24"
                    alt=""
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box mb="20px">
        <Typography
          sx={{
            color: "#323232",
            fontSize: "24px",
            fontWeight: 600,
            lineHeight: "35px",
          }}
        >
          Upload {uploadMode} Form
        </Typography>
        <Typography
          sx={{
            color: "#A2A2A2",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "24px",
          }}
        >
          Complete your departure process seamlessly by uploading the departure
          form here.
        </Typography>
        <Box sx={{ mt: "20px" }}>
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "31px",
            }}
          >
            Form
          </Typography>
          <Box
            sx={{ mt: "15px", display: "flex", gap: "20px", flexWrap: "wrap" }}
          >
            <UploadButton
              mimeType={["*"]}
              allowMultiple={false}
              id="forms"
              onchange={handleDocChange}
            />
            {docs.map((doc, i) => (
              <Box
                sx={{
                  position: "relative",
                  height: {
                    md: "192px",
                    lg: "192px",
                    xl: "192px",
                    sm: "125px",
                    xs: "125px",
                  },
                  width: {
                    md: "192px",
                    lg: "192px",
                    xl: "192px",
                    sm: "125px",
                    xs: "125px",
                  },
                  borderRadius: "10px",
                }}
              >
                <Box
                  component="img"
                  src="/assets/img/icons/form.svg"
                  sx={{
                    height: {
                      md: "192px",
                      lg: "192px",
                      xl: "192px",
                      sm: "125px",
                      xs: "125px",
                    },
                    width: {
                      md: "192px",
                      lg: "192px",
                      xl: "192px",
                      sm: "125px",
                      xs: "125px",
                    },
                    borderRadius: "10px",
                  }}
                />
                <Box
                  sx={{
                    background: "#307AFF",
                    position: "absolute",
                    bottom: 0,
                    width: "100%",

                    borderBottomLeftRadius: "10px",
                    borderBottomRightRadius: "10px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#FFF",
                      fontWeight: "600",
                      fontSize: "14px",
                      lineHeight: "20px",
                      wordWrap: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                      m: "10px",
                    }}
                  >
                    {doc.name}
                  </Typography>
                </Box>
                <Box
                  sx={{ position: "absolute", top: "-10px", right: "-10px" }}
                  onClick={() => removeDocFile(i)}
                >
                  <img
                    src="/assets/img/icons/close_red.svg"
                    width="24"
                    height="24"
                    alt=""
                  />
                </Box>
              </Box>
            ))}
            <Box></Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: "30px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "15px",
        }}
      >
        <Button
          variant="outlined"
          sx={{ borderRadius: "10px", height: "56px", width: "176px" }}
          size="large"
          onClick={() => setUploadMode("")}
          disabled={uploadImages}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{ borderRadius: "10px", height: "56px", width: "176px" }}
          size="large"
          onClick={proceedToSaveFiles}
          disabled={uploadImages}
        >
          Upload
        </Button>
      </Box>

      {uploadImages && (
        <Backdrop open style={{ zIndex: 9999 }}>
          <Box
            borderRadius={5}
            component={Paper}
            p={3}
            display="flex"
            flexDirection="column"
            gap={2}
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
            <Typography>Uploading files, Please wait</Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
};

export default BookingDocumentsUpload;
